import React, { useEffect, useState } from 'react';
import { Col } from 'react-bootstrap';
import { FaTrash, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

import SubAbas from './subabas';
import BarraInferiorBotoes from '../../../components/BarraInferiorBotoes';
import BarraSubAbas from '../../../components/BarraSubAbas';
import BoxConteudo from '../../../components/BoxConteudo';
import ContainerConteudo from '../../../components/ContainerConteudo';
import CustomCol from '../../../components/CustomCol';
import CustomRow from '../../../components/CustomRow';
import Input from '../../../components/InputNew';
import Select from '../../../components/InputNew/Select';
import SelectInput from '../../../components/InputNew/Select';
import Log from '../../../components/Log/Log';
import Modal from '../../../components/Modal';
import Status from '../../../components/Status';
import Tabela from '../../../components/Tabela';
import WorkspaceBotoes from '../../../components/WorkspaceBotoes';
import WorkspaceBotoesNew from '../../../components/WorkspaceBotoes/indexNew';
import WorkspaceFicha from '../../../components/WorkspaceFicha';
import { getUser } from '../../../modules/contexts/Auth/RequireAuth';
import { usePasswordValidation } from '../../../modules/hooks/usePasswordValidation';
import { useCor } from '../../../services/enum/useCorPele';
import { useGeneros } from '../../../services/enum/useGeneros';
import httpClient from '../../../services/httpClient';
import { getUserData } from '../../../services/userService';
import { useUserIdData } from '../../../services/usuario/useUserIdData';
import { formataNomeMeioSiglas, formatDate } from '../../../utils/functions';
import { swal, swalAtualizacao } from '../../../utils/swal';

const MeusDadosUsuario: React.FC = () => {
  const { formData, setFormData } = useUserIdData();
  const [showModal, setShowModal] = useState<boolean>(false);

  const [senha, setSenha] = useState<string>('');
  const [confirmaSenha, setConfirmaSenha] = useState<string>('');
  const [statusStyle] = useState<React.CSSProperties>({});

  const user = getUser();
  const userID = user.sub;

  const [validLength, passwordMatch] = usePasswordValidation({
    firstPassword: senha,
    secondPassword: confirmaSenha,
  });
  const [passwordDigits, setPasswordDigits] = useState<number>(0);

  const generos = useGeneros();
  const cores = useCor();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getUserData(userID);
        setFormData(userData);
      } catch (error) {
        console.error('Erro ao buscar dados do usuário:', error);
        swal('error', 'Erro', 'Erro ao carregar dados do usuário.');
      }
    };
    if (userID) fetchUserData();
  }, [userID, setFormData]);

  const openSenhaModal = () => setShowModal(true);
  const closeSenhaModal = () => setShowModal(false);

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
      | { name: string; value: any },
  ) => {
    const { name, value } = 'target' in event ? event.target : event;
    const enumFields = [
      'genero',
      'cor',
      'tipoVinculo',
      'orgaoOrigem',
      'unidadeDAF',
    ];
    const newValue = enumFields.includes(name)
      ? parseInt(value as string, 10)
      : value;

    setFormData((prev: any) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSenhaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSenha(e.target.value);
    setPasswordDigits(e.target.value.length);
  };

  const handleConfirmaSenhaChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setConfirmaSenha(e.target.value);
  };

  const validaSenhaTamanho = () => {
    if (passwordDigits > 0 && !validLength) {
      swal(
        'error',
        'Senha inválida',
        'A senha deve conter no mínimo 6 caracteres!',
      );
      setSenha('');
    }
  };

  const validaSenhaConfirma = () => {
    if (passwordDigits >= 6 && !passwordMatch) {
      swal('error', 'Senha inválida', 'As senhas digitadas não são iguais!');
      setConfirmaSenha('');
    }
  };

  const updateData = async () => {
    try {
      const response = await httpClient.patch(`/api/users/${userID}`, formData);
      swalAtualizacao('Sucesso', response.data.message);
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      swal('error', 'Erro', 'Alterações não foram realizadas com sucesso.');
    }
  };

  const updateSenha = async () => {
    try {
      if (!validLength || !passwordMatch) {
        throw new Error(
          'As senhas devem ter no mínimo 6 caracteres e serem iguais.',
        );
      }
      const response = await httpClient.patch(`/api/users/${userID}/senha`, {
        novaSenha: senha,
      });
      if (response.status === 200) {
        setShowModal(false);
        setSenha('');
        setConfirmaSenha('');
        swalAtualizacao('Sucesso', response.data.message);
      }
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      swal(
        'error',
        'Erro ao alterar senha',
        'As senhas devem ter no mínimo 6 caracteres e serem iguais.',
      );
    }
  };

  const normalizarCNPJ = (cnpj: string): string => {
    if (!cnpj || cnpj === '---') return '---';
    return cnpj.replace(/\D/g, '');
  };

  interface Fornecedor {
    id?: number;
    cnpj: string;
    razaoSocial: string;
  }

  interface VinculoFornecedor {
    id: string | number;
    statusInterface: string; // UI usa este nome
    tpVinculo: number;
    fornecedor: Fornecedor;
    fornecedorDetalhado?: Fornecedor;
    dataVinculacao: string | null;
    usuarioVinculacao: {
      nomeCompleto?: string;
    } | null;
    observacao: string;
    stRegistroAtivo?: string;
  }

  interface DetalhesFornecedor {
    cnpj: string;
    razaoSocial: string;
    [key: string]: any;
  }

  const [showDetalhesFornecedor, setShowDetalhesFornecedor] = useState(false);
  const [fornecedorSelecionada, setFornecedorSelecionada] =
    useState<VinculoFornecedor | null>(null);
  const [, setDesvinculando] = useState(false);
  const [fornecedorVinculadas, setFornecedorVinculadas] = useState<
    VinculoFornecedor[]
  >([]);

  const getFornecedorDetalhes = async (
    id: number,
  ): Promise<DetalhesFornecedor> => {
    try {
      const response = await httpClient.get(`/api/fornecedor/buscar`, {
        params: { id },
      });
      return {
        ...response.data,
        cnpj: response.data?.cnpj ? normalizarCNPJ(response.data.cnpj) : '---',
        razaoSocial: response.data?.razaoSocial || '---',
      };
    } catch (error) {
      console.error('Erro ao buscar fornecedor:', error);
      return { cnpj: '---', razaoSocial: '---' };
    }
  };

  const carregarVinculosAtivos = async () => {
    try {
      const response = await httpClient.get(`/api/usuariointerface`, {
        params: { coUsuario: userID, stRegistroAtivo: 'S' },
      });

      const vinculos = response.data.relacoes || [];

      const relacoesCompletas: VinculoFornecedor[] = await Promise.all(
        vinculos
          .filter((v: any) => v.statusRegistroAtivo === 'S')
          .map(async (v: any) => {
            const fornecedorId = Number(
              v?.fornecedor?.id ?? v?.coFornecedor ?? NaN,
            );
            let detalhes: DetalhesFornecedor | null = null;
            if (Number.isFinite(fornecedorId)) {
              detalhes = await getFornecedorDetalhes(fornecedorId);
            }
            return {
              id: v.id,

              statusInterface: v.stInterface ?? v.statusInterface ?? 'N',
              tpVinculo: parseInt(v.tpVinculo, 10) || 2,
              fornecedor: {
                id: Number.isFinite(fornecedorId) ? fornecedorId : undefined,
                cnpj: detalhes?.cnpj ?? '---',
                razaoSocial: detalhes?.razaoSocial ?? '---',
              },
              fornecedorDetalhado: detalhes ?? undefined,
              dataVinculacao: v.dataVinculacao || null,
              usuarioVinculacao: v.usuarioVinculacao || null,
              observacao: v.observacao || '---',
              stRegistroAtivo: v.statusRegistroAtivo || 'N',
            };
          }),
      );

      setFornecedorVinculadas(relacoesCompletas);
    } catch (error) {
      console.error('Erro ao buscar fornecedores vinculados:', error);
    }
  };

  useEffect(() => {
    const fetchVinculosAtivos = async () => {
      try {
        const response = await httpClient.get(`/api/usuariointerface`, {
          params: { coUsuario: userID, stRegistroAtivo: 'S' },
        });

        const vinculos = response.data.relacoes || [];

        const relacoesCompletas: VinculoFornecedor[] = await Promise.all(
          vinculos
            .filter((v: any) => v.statusRegistroAtivo === 'S')
            .map(async (v: any) => {
              return {
                id: v.id,
                statusInterface: v.stInterface ?? v.statusInterface ?? 'N',
                tpVinculo: parseInt(v.tpVinculo, 10) || 2,
                fornecedor: {
                  id: v.fornecedor?.id ?? undefined,
                  cnpj: v.cnpj || '---',
                },
                dataVinculacao: v.dataVinculacao || null,
                usuarioVinculacao: v.usuarioVinculacao || null,
                observacao: v.observacao || '---',
                stRegistroAtivo: v.statusRegistroAtivo || 'N',
              };
            }),
        );

        setFornecedorVinculadas(relacoesCompletas);
      } catch (error) {
        console.error('Erro ao buscar fornecedores vinculados:', error);
      }
    };

    fetchVinculosAtivos();
    carregarVinculosAtivos();
  }, [userID]);

  const formatarCNPJ = (cnpj: string): string => {
    const cnpjLimpo = (cnpj || '').replace(/\D/g, '');
    if (!cnpjLimpo) return '---';
    return cnpjLimpo.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      '$1.$2.$3/$4-$5',
    );
  };

  const headersFornecedor = [
    { text: '', width: 2, key: '' },
    { text: 'ID', width: 2, key: 'id' },
    { text: 'Fornecedor', key: 'razaoSocial', width: 20 },
    { text: 'CNPJ', key: 'cnpj', width: 15 },
    { text: 'Data de Vínculo', key: 'dataVinculo', width: 15 },
    { text: 'Responsável pelo Vínculo', key: 'usuarioVinculacao', width: 20 },
    { text: 'Tipo de Vínculo', key: 'tpVinculo', width: 15 },
    { text: 'Status', key: 'status', width: 10 },
  ];

  const tableDataFornecedor = React.useMemo(() => {
    return fornecedorVinculadas
      .filter((item) => item.stRegistroAtivo?.toUpperCase().trim() === 'S')
      .map((item) => ({
        id: item.id,
        razaoSocial: item.fornecedor.razaoSocial ?? '---',
        cnpj: formatarCNPJ(item.fornecedor.cnpj ?? ''),
        dataVinculo: item.dataVinculacao
          ? formatDate(item.dataVinculacao)
          : '---',
        usuarioVinculacao:
          formataNomeMeioSiglas(item.usuarioVinculacao?.nomeCompleto || '') ||
          '---',
        tpVinculo:
          item.tpVinculo === 1
            ? 'Representante Técnico'
            : item.tpVinculo === 2
              ? 'Usuário'
              : '---',
        status: (
          <Status
            label={item.statusInterface === 'S' ? 'Ativo' : 'Pendente'}
            color={
              item.statusInterface === 'S'
                ? 'rgba(9, 175, 9, 0.655)'
                : 'rgba(200, 50, 50, 0.7)'
            }
            icon={
              item.statusInterface === 'S'
                ? FaCheckCircle
                : FaExclamationTriangle
            }
          />
        ),
      }));
  }, [fornecedorVinculadas]);

  const handleDesvincularFornecedor = async (id: number) => {
    try {
      setDesvinculando(true);
      await httpClient.delete(`/api/usuariointerface/${id}`);
      swalAtualizacao('Sucesso', 'Fornecedor desvinculado com sucesso.');
      setShowDetalhesFornecedor(false);
      await carregarVinculosAtivos();
    } catch (error) {
      console.error('Erro ao desvincular fornecedor:', error);
      swal(
        'error',
        'Erro ao desvincular fornecedor',
        'Tente novamente mais tarde.',
      );
    } finally {
      setDesvinculando(false);
    }
  };

  const handleConfirmarVinculo = async (id: number) => {
    try {
      if (!fornecedorSelecionada)
        throw new Error('Nenhum fornecedor selecionado');

      const agoraISO = new Date().toISOString();
      const payload = {
        coUsuario: Number(user.sub),
        coUsuarioVinculacao: Number(user.sub),

        cnpj: (fornecedorSelecionada.fornecedor.cnpj || '').replace(/\D/g, ''),
        tpVinculo: fornecedorSelecionada.tpVinculo ?? 2,

        stInterface: 'S',
        stRegistroAtivo: 'S',
        dtVinculacao: fornecedorSelecionada.dataVinculacao ?? agoraISO,
      };

      await httpClient.patch(`/api/usuariointerface/${id}`, payload);

      setFornecedorVinculadas((prev) =>
        prev.map((r) =>
          String(r.id) === String(id)
            ? {
                ...r,
                statusInterface: 'S',
                stRegistroAtivo: 'S',
                dataVinculacao: payload.dtVinculacao,
              }
            : r,
        ),
      );
      setFornecedorSelecionada((prev) =>
        prev && String(prev.id) === String(id)
          ? {
              ...prev,
              statusInterface: 'S',
              stRegistroAtivo: 'S',
              dataVinculacao: payload.dtVinculacao,
            }
          : prev,
      );

      swalAtualizacao('Sucesso', 'Vínculo confirmado com sucesso.');
      setShowDetalhesFornecedor(false);
    } catch (error: any) {
      console.error('Erro ao confirmar vínculo:', error);
      swal(
        'error',
        'Erro ao confirmar vínculo',
        error?.message ?? 'Tente novamente mais tarde.',
      );
    }
  };

  const closeFornecedorModal = () => setShowDetalhesFornecedor(false);

  return (
    <>
      <BarraSubAbas subAbas={SubAbas} />

      <ContainerConteudo>
        <WorkspaceFicha>
          <Modal
            icone="key"
            tituloModal="Alterar Senha"
            open={showModal}
            onHide={closeSenhaModal}
            modalSize="medio"
          >
            <div className="d-flex form flex-column justify-content-center align-items-center gap-4 m-3">
              <Input
                name="senha"
                id="senha"
                value={senha}
                label="Nova Senha"
                onChange={handleSenhaChange}
                onBlur={validaSenhaTamanho}
                type="password"
              />
              <Input
                name="confirmaSenha"
                value={confirmaSenha}
                label="Confirma Senha"
                id="confirmaSenha"
                onChange={handleConfirmaSenhaChange}
                type="password"
                onBlur={validaSenhaConfirma}
              />
              <WorkspaceBotoes
                cancelar
                onClickCancelar={closeSenhaModal}
                save
                onClickSave={updateSenha}
              />
            </div>
          </Modal>

          <BoxConteudo boxTitulo="Dados do Usuário">
            <CustomRow style={{ marginTop: '-15px' }}>
              <CustomCol width="12%">
                <SelectInput
                  name="stRegistroAtivo"
                  options={[
                    { label: 'Inativo', value: '0' },
                    { label: 'Ativo', value: '1' },
                  ]}
                  label="Status"
                  value={formData.stRegistroAtivo === 'S' ? '1' : '0'}
                  onChange={handleChange}
                  style={statusStyle}
                  disabled
                />
              </CustomCol>
              <CustomCol width="13%">
                <Input
                  name="cpf"
                  mascara="cpf"
                  value={formData.cpf ?? ''}
                  id="cpf"
                  label="CPF"
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="32%">
                <Input
                  name="nomeCompleto"
                  value={formData.nomeCompleto ?? ''}
                  id="nome"
                  label="Nome completo"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="dataNascimento"
                  type="date"
                  value={formData.dataNascimento ?? ''}
                  id="dataNascimento"
                  label="Data de nascimento"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Select
                  name="genero"
                  value={formData.genero != null ? String(formData.genero) : ''}
                  onChange={handleChange}
                  options={generos}
                  label="Selecione o gênero"
                />
              </CustomCol>
              <CustomCol>
                <Select
                  name="cor"
                  value={formData.cor != null ? String(formData.cor) : ''}
                  onChange={handleChange}
                  options={cores}
                  label="Cor da pele"
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol width="13%">
                <Input
                  name="celular"
                  mascara="celular"
                  value={formData.celular ?? ''}
                  id="celular"
                  label="Celular"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="emailPessoal"
                  value={formData.emailPessoal ?? ''}
                  id="emailPessoal"
                  label="Email pessoal"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="linkedin"
                  value={formData.linkedin ?? ''}
                  id="linkedin"
                  label="LinkedIn"
                  onChange={handleChange}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="lattes"
                  value={formData.lattes ?? ''}
                  id="lattes"
                  label="Lattes"
                  onChange={handleChange}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>

          <BoxConteudo boxTitulo="Fornecedores Vinculados">
            <Tabela
              headers={headersFornecedor}
              dataTable={tableDataFornecedor}
              rowClickable={true}
              showPagination={false}
              onRowClick={(payload: any) => {
                const clickedId =
                  payload && typeof payload === 'object'
                    ? 'id' in payload
                      ? (payload as any).id
                      : ((payload as any)?.original?.id ??
                        (payload as any)?.row?.id ??
                        (payload as any)?.values?.id)
                    : payload;

                let fornecedor: VinculoFornecedor | undefined;

                if (clickedId != null) {
                  fornecedor = fornecedorVinculadas.find(
                    (item) => String(item.id) === String(clickedId),
                  );
                }

                if (!fornecedor && Number.isInteger(clickedId)) {
                  const idx = Number(clickedId);
                  fornecedor = fornecedorVinculadas[idx];
                }

                if (!fornecedor) {
                  console.warn(
                    '[Tabela.onRowClick] Fornecedor não localizado para payload:',
                    payload,
                  );
                  return;
                }

                setFornecedorSelecionada(fornecedor);
                setShowDetalhesFornecedor(true);
              }}
            />
          </BoxConteudo>
        </WorkspaceFicha>
      </ContainerConteudo>

      {showDetalhesFornecedor && fornecedorSelecionada && (
        <Modal
          tituloModal="Detalhes do Vínculo"
          open={showDetalhesFornecedor}
          onHide={closeFornecedorModal}
        >
          <WorkspaceFicha>
            <Log
              dataRegistro={fornecedorSelecionada.dataVinculacao || undefined}
            />

            <CustomRow>
              <CustomCol width="30%">
                <Input
                  id="cnpj-fornecedor"
                  label="CNPJ"
                  value={formatarCNPJ(
                    fornecedorSelecionada.fornecedorDetalhado?.cnpj ??
                      fornecedorSelecionada.fornecedor.cnpj ??
                      '',
                  )}
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="70%">
                <Input
                  id="razao-social"
                  label="Razão Social"
                  value={
                    fornecedorSelecionada.fornecedorDetalhado?.razaoSocial ??
                    fornecedorSelecionada.fornecedor.razaoSocial ??
                    '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="50%">
                <Input
                  id="data-vinculacao"
                  label="Data de Vinculação"
                  value={
                    fornecedorSelecionada.dataVinculacao
                      ? formatDate(fornecedorSelecionada.dataVinculacao)
                      : '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="50%">
                <Input
                  id="status-vinculacao"
                  label="Status"
                  value={
                    fornecedorSelecionada.statusInterface === 'S'
                      ? 'Ativo'
                      : 'Pendente'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="50%">
                <Input
                  id="tipo-vinculo"
                  label="Tipo de Vínculo"
                  value={
                    fornecedorSelecionada.tpVinculo === 1
                      ? 'Representante Técnico'
                      : fornecedorSelecionada.tpVinculo === 2
                        ? 'Usuário'
                        : '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
              <CustomCol width="50%">
                <Input
                  id="responsavel-vinculacao"
                  label="Responsável pelo Vínculo"
                  value={
                    formataNomeMeioSiglas(
                      fornecedorSelecionada.usuarioVinculacao?.nomeCompleto ||
                        '',
                    ) || '---'
                  }
                  onChange={handleChange}
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <Col>
                <Input
                  id="observacoes-vinculacao"
                  label="Observações"
                  value={fornecedorSelecionada.observacao || '---'}
                  type="textarea"
                  onChange={handleChange}
                  readonly
                />
              </Col>
            </CustomRow>

            <BarraInferiorBotoes>
              {fornecedorSelecionada.statusInterface === 'S' ? (
                <WorkspaceBotoesNew
                  label="Desvincular"
                  tipo="cancel"
                  icone={<FaTrash />}
                  onClick={() =>
                    handleDesvincularFornecedor(
                      Number(fornecedorSelecionada.id),
                    )
                  }
                  style={{ marginRight: '8px' }}
                />
              ) : (
                <WorkspaceBotoesNew
                  label="Confirmar"
                  tipo="save"
                  icone={<FaCheckCircle />}
                  onClick={() =>
                    handleConfirmarVinculo(Number(fornecedorSelecionada.id))
                  }
                  style={{ marginRight: '8px' }}
                />
              )}
            </BarraInferiorBotoes>
          </WorkspaceFicha>
        </Modal>
      )}

      <BarraInferiorBotoes>
        <WorkspaceBotoes
          save
          onClickSave={updateData}
          senha
          onClickSenha={openSenhaModal}
        />
      </BarraInferiorBotoes>
    </>
  );
};

export default MeusDadosUsuario;
